<template>
  <div class="sigrndit">
    <div class="text-large weight_700 clr-primary">
      {{ $t('nodeedit.title') }}
    </div>

    <div
      v-if="loading || error"
      class="f-center gw_box"
      style="--clr-box-back: var(--clr-black-1-3)"
      :style="`min-height: ${l_actions.length * 2.5 - 0.5 + 5}rem`"
    >
      <LoaderSpinner
        v-if="loading"
        class="anim-fade-in"
        :style="`--size:2.5rem`"
        style="margin: 1rem"
      />
      <IconAndText
        v-else-if="error"
        class="anim-fade-in"
        icon="mdi-alert-circle-outline"
        style="--icon-color: var(--clr-error); --icon-size: 4rem"
        :title="$t('messages.oops_error')"
        :subtitle="$t('messages.opps_error_sub')"
      />
    </div>
    <div
      v-else-if="aaNodeedits"
      class="sigrndit_editnode anim-fade-in"
    >
      <div class="sigrndit_actions">
        <div
          v-for="(aa, idx) in legalActions.filter(
            i => i.type !== 'RAISE' && !l_actions.some(a => i.type === a.type),
          )"
          :key="idx + 'l'"
          class="sigrndit_action sigrndit_action_possible"
          :style="`order: ${legalActions.find(i => i.type === aa.type).order}`"
          @click="addAction(aa)"
        >
          <div class="sigrndit_action_left">
            <div class="sigrndit_action_name clr-grey">
              {{ aa.display_name }}
            </div>
          </div>
          <div class="sigrndit_action_right">
            <div
              class="sigrndit_action_add mdi icon_btn mdi-plus-circle csst"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
            >
              <div
                class="csst_content clr-white"
                style="position: fixed"
                v-html="$t('tooltips.add_action')"
              ></div>
            </div>
          </div>
        </div>

        <div
          v-for="(aa, idx) in l_actions"
          :key="idx + 'a'"
          class="sigrndit_action"
          :style="`order: ${legalActions.find(i => i.type === aa.type).order}`"
          :data-tst="aa.type"
        >
          <div class="sigrndit_action_left">
            <div
              class="sigrndit_action_name"
              :class="{
                'clr-grey': isAllInBeforeAllin(aa, idx) || isSameBetBeforeBet(aa, idx),
              }"
            >
              {{ aa.display_name }}
            </div>
            <div
              v-if="aa.type === 'RAISE'"
              class="sigrndit_action_bet"
            >
              <NumberInputUnit
                :ref="`input_${idx}`"
                :inputProps="{
                  stopOnMin: true,
                  focusOnMounted: true,
                  min: aa[`min_${aa.unit === '%' ? 'percentage_input' : aa.unit}`],
                  max: aa[`max_${aa.unit === '%' ? 'percentage_input' : aa.unit}`],
                  step: aa.unit === 'x' ? 0.1 : aa.unit === 'bb' ? raiseBetConf.step : 1,
                  decimals:
                    aa.unit === 'bb'
                      ? unitDecimals.bb
                      : aa.unit === '%'
                        ? unitDecimals.bb_pot
                        : aa.unit === 'x'
                          ? unitDecimals.x
                          : aa.unit === 'e'
                            ? unitDecimals.e
                            : 0,
                }"
                style="--input-units-width: 8rem"
                :inputValue="
                  aa.unit === '%'
                    ? numFixedUnit(aa.nd_percentage * 100, 'bb_pot')
                    : aa.unit === 'bb'
                      ? numFixedUnit(aa.nd_bb, 'bb')
                      : aa.unit === 'x'
                        ? aa.nd_x
                        : aa.nd_e
                "
                :unitValue="aa.unit"
                :unitsOptions="unitsOptions"
                @input:all="val => handleInputAll(val, idx)"
              >
              </NumberInputUnit>
              <div
                v-if="aa.unit !== 'e' && aa.unit !== 'x'"
                class="sigrndit_action_bet_percent truncate"
                :title="getBBPercent(aa)"
              >
                <!-- v-if="!isPreflop && aa.unit !== 'e' && aa.unit !== 'x'" -->
                ({{ getBBPercent(aa) }})
              </div>
            </div>

            <RangeInput
              v-if="!onlyAllin && aa.type === 'RAISE' && aa.unit !== 'e' && aa.unit !== 'x'"
              class="sigrndit_action_slider"
              :values="[
                {
                  id: 0,
                  cssStyle: `
                --clr-input-back: var(--clr-black-3);
                --clr-input-handle: var(--clr-primary);
                `,
                  percentage: aa.percentageOfMax,
                  title: $t('common.from'),
                },
                {
                  id: 1,
                  cssStyle: `--clr-input-back: var(--clr-black-3);`,
                  percentage: 1 - aa.percentageOfMax,
                },
              ]"
              @values:update="({ values }) => handleRangeInput(values, idx)"
            >
            </RangeInput>
          </div>
          <div class="sigrndit_action_right">
            <div
              class="sigrndit_action_remove mdi icon_btn mdi-close csst"
              :data-tst="`remove_${aa.type.toLowerCase()}_${idx}`"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
              @click="removeAction(idx)"
            >
              <div
                class="csst_content clr-white"
                style="position: fixed"
                v-html="$t('tooltips.remove_action')"
              ></div>
            </div>
          </div>
        </div>

        <div
          v-for="(aa, idx) in hasRaiseBet && !betRaiseLimitReached
            ? [legalActions.find(i => i.type === 'RAISE')].filter(i => i)
            : []"
          :key="idx + 'r'"
          class="sigrndit_action sigrndit_action_possible"
          style="order: 4"
          @click="addBetRaise"
        >
          <div class="sigrndit_action_left">
            <div class="sigrndit_action_name clr-grey">
              {{ aa.display_name }}
            </div>
          </div>
          <div class="sigrndit_action_right">
            <div
              class="sigrndit_action_add mdi icon_btn mdi-plus-circle csst"
              @mouseenter="e => fl_positionTooltip(e, { placement: 'top' })"
            >
              <div
                class="csst_content clr-white"
                style="position: fixed"
                v-html="$t('tooltips.add_action')"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <portal to="dialog_actions">
      <div
        class="gw_btn-v1 gw_btn_text mr-auto"
        @click="reset()"
      >
        <i class="mdi mdi-replay mr-1"></i>
        {{ $t('btn.reset') }}
      </div>
      <div class="csst ml-auto">
        <div
          class="gw_btn-v1 gw_btn_primary"
          :class="{ 'gw_btn_disabled no-events': !canApply }"
          style="--clr-disabled: var(--clr-grey-2)"
          data-tst="confirm"
          @click="apply()"
        >
          <LoaderSpinner
            v-show="loadingNewSol"
            style="--size: 1.5rem; position: absolute"
          />
          <span :class="{ opacity_0: loadingNewSol }">
            {{ $t('btn.confirm') }}
          </span>
        </div>
        <div
          v-if="!canApply"
          class="csst_content text-nowrap"
          style="top: -2rem; right: 0; position: absolute"
        >
          {{ $t('nodeedit.select_min_one_action') }}
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import NumberInputUnit from '@/components/lib/NumberInputUnit'
import IconAndText from '@/components/Poker/Overlays/IconAndText'
import LoaderSpinner from '@/components/Loaders/LoaderSpinner'
import RangeInput from '@/components/lib/RangeInput'
import { getHistoryUrlParams, getCurrentBetAsPotFromHistory } from '@/store/gamestate/gamestate'
import {
  getSolfigrFields,
  getBlinds,
  logposition,
  logslider,
} from '@/store/solfigr/solfigr_helpers'
import { unitDecimals, all_streets } from '@/helpers/constants'
import { SNK } from '@/helpers/helpers'

import float_mix from '@/mixins/float_mix'
import format_mix from '@/mixins/format_mix'

import findLastIndex from 'lodash/findLastIndex'

export default {
  name: 'SolfigrNodeEdit',
  components: {
    NumberInputUnit,
    IconAndText,
    LoaderSpinner,
    RangeInput,
  },
  mixins: [format_mix, float_mix],
  props: {
    namespace: { required: true, type: String },
    primaryNamespace: { required: true, type: String },
    editTree: Boolean,
    editSolution: Boolean,
  },
  emits: ['close'],
  data() {
    this.conf = getSolfigrFields()
    this.unitDecimals = unitDecimals
    return {
      loading: false,
      error: false,
      aaNodeedits: null,
      loadingNewSol: false,
      l_actions: [],
      l_hSpot: null,
      l_hSpots: [],

      lastUsedUnit: '', // initialized from localStorage or from showBetIn - primary is localStorage
    }
  },
  created() {
    // some other node could be loading in the background - so work with a copy
    const hSpot = this.$store.getters[`${this.namespace}/history_spot`]
    const hSpots = this.$store.getters[`${this.namespace}/history_spots`]
    this.l_hSpot = JSON.parse(JSON.stringify(hSpot))
    this.l_hSpots = JSON.parse(JSON.stringify(hSpots))

    this.reset()
    if (this.editSolution) {
      this.getAvailableNodeEdits()
    }
    if (this.editTree) {
      this.aaNodeedits = hSpot.available_node_edits
      this.initActions()
    }
  },
  methods: {
    reset() {
      if (this.loading) return
      this.l_actions = JSON.parse(JSON.stringify(this.l_hSpot?.available_actions))
      this.initActions()
    },
    async getAvailableNodeEdits() {
      this.loading = true
      let hSpots = this.l_hSpots
      const hSpot = this.l_hSpot
      const { solElite } = this
      // console.log(solElite)

      hSpots = hSpots.filter(i => i.id < hSpot.id)
      const hp = getHistoryUrlParams({ history_spots: hSpots, includeShowdown: false })

      const data = {
        custom_solution_id: solElite.custom_solution_id,
        preflop_actions: hp.preflop_actions,
        flop_actions: hp.flop_actions,
        turn_actions: hp.turn_actions,
        river_actions: hp.river_actions,
      }
      const result = await this.$store.dispatch('api_call', {
        method: 'post',
        data,
        endpoint: 'poker/custom-solutions/available-node-edits/',
        hasCancel: true,
      })

      this.aaNodeedits = result?.response?.data || null

      this.initActions()

      this.loading = false
      this.error = result?.error
    },
    initActions() {
      this.setLastUsedUnit(this.unit)
      this.setActions()
    },
    setActions() {
      if (!this.aaNodeedits) return
      this.l_actions = this.l_actions.map(a => {
        if (a.type === 'RAISE') {
          // max_bb is returned rounded to 0 decimals - whereas raise_amount is rounded to 3 decimals
          // and so we need to do this using allin flag
          const allin = a.allin || a.raise_amount >= this.unitMinMaxes.max_bb
          const raise_amount = allin ? this.unitMinMaxes.max_bb : a.raise_amount

          const { currentBetAsPot: nd_percentage } = this.getCurretnBetsFromBet({
            raise_amount,
            amount_by_pot: null,
          })
          const r = {
            ...a,
            ...this.unitMinMaxes,
            allin,
            nd_bb: raise_amount,
            nd_percentage,
            nd_e: 1,
            nd_x: 1,
            percentageOfMax: logposition({
              value: nd_percentage,
              min: this.unitMinMaxes.min_percentage,
              max: this.unitMinMaxes.max_percentage,
            }),
            display_name: allin ? 'Allin' : this.raiseIsBet ? 'Bet' : 'Raise',
            unit: this.unit,
          }
          return r
        }
        return a
      })
    },
    addAction(action) {
      this.l_actions = [action, ...this.l_actions]
    },
    addBetRaise() {
      if (this.betRaiseLimitReached) return
      const action = this.legalActions.find(i => i.type === 'RAISE')
      this.l_actions = [...this.l_actions, { ...action }]
    },
    removeAction(idx) {
      this.l_actions = this.l_actions.filter((a, i) => i !== idx)
    },
    getCurretnBetsFromBet({ raise_amount, betsize_by_pot }) {
      const { currentBetAsPot, currentBet } = getCurrentBetAsPotFromHistory({
        history_spots: this.l_hSpots,
        history_spot: this.l_hSpot,
        street: this.l_hSpot.street,
        currentBet: raise_amount,
        currentBetAsPot: betsize_by_pot,
        blinds: this.blinds,
      })
      return { currentBetAsPot, currentBet }
    },
    handleRangeInput(values, idx) {
      let percentageOfMax = values[0].percentage // linear value
      const max = this.unitMinMaxes.max_percentage
      const min = this.unitMinMaxes.min_percentage

      if (percentageOfMax === 1) {
        // oferbuff it to make sure 1 is always All-in in the end
        // cos this log-function sometimes returns 99.9998% or such
        percentageOfMax = Infinity
      }

      const nd_percentage = logslider({ position: percentageOfMax, min, max })

      let numVal = 0
      const { unit } = this.l_actions[idx]

      const { currentBetAsPot, currentBet } = this.getCurretnBetsFromBet({
        raise_amount: null,
        betsize_by_pot: nd_percentage,
      })
      if (unit === '%') {
        numVal = this.numFixedUnit(currentBetAsPot * 100, 'bb_pot')
      } else {
        numVal = this.numFixedUnit(currentBet, 'bb')
      }

      // numVal = this.numFixed(numVal, 1)
      const l_inputVals = {
        numVal,
        val: '',
        origVal: '',
      }
      // debugger
      this.handleInputAll(l_inputVals, idx)
    },
    handleInputAll(inputVals, idx) {
      let num = inputVals.numVal
      const possibleUnits = ['b', ...this.unitsOptions]
      let unit = possibleUnits.find(uu => inputVals.val.includes(uu)) || this.l_actions[idx].unit
      unit = unit === 'b' ? 'bb' : unit

      const unitChange = unit !== this.l_actions[idx].unit
      this.l_actions[idx].unit = unit
      this.setLastUsedUnit(unit)

      // console.log('--', inputVals, unit, unitChange)
      if (unit !== 'e' && unit !== 'x') {
        if (unitChange) {
          num = unit === '%' ? this.l_actions[idx].nd_percentage * 100 : this.l_actions[idx].nd_bb
        }

        let { currentBetAsPot: nd_percentage, currentBet: nd_bb } = this.getCurretnBetsFromBet({
          raise_amount: unit === '%' ? null : num,
          betsize_by_pot: unit === '%' ? num / 100 : null,
        })

        const { max_bb } = this.unitMinMaxes
        const { min_bb } = this.unitMinMaxes
        const max = this.unitMinMaxes.max_percentage
        const min = this.unitMinMaxes.min_percentage

        nd_bb = Math.max(Math.min(nd_bb, max_bb), min_bb)
        nd_percentage = Math.max(Math.min(nd_percentage, max), min)

        // handle rounding errors - set all-in for %
        if (unit === '%' && num === this.unitMinMaxes.max_percentage_input) {
          nd_bb = max_bb
        }

        // console.log({
        //   max_bb,
        //   min_bb,
        //   max,
        //   min,
        //   nd_percentage,
        //   nd_bb,
        //   max_input,
        //   nd_percentage_input,
        //   unit,
        //   num,
        // })

        // test links with close allin rounding
        // http://localhost:8080/solutions?custree_id=ba343bea-d4ce-44f0-9a32-0aeff14e4d09&solution_type=custom&gametype=Cash6m500zBasic&depth=100&tmpId=1700574926103&soltab=range&gmff_type=basic&gmfft_sort_key=0&gmfft_sort_order=desc&board=9h8c7c8s4d&cussol_id=5a9defb0-b963-4edb-801c-34eb24397cc2&flop_actions=X-X&turn_actions=X-X&river_actions=X-R3.4-R15.2&history_spot=7&dialogs=solfigr-nodeedit-dialog_edit-solution_tmpNamespace-tmp%2Fprimary
        // http://localhost:8080/solutions?custree_id=a8b19290-ed41-44d6-bc6d-18031354dfd8&solution_type=custom&gametype=Cash6m500zBasic&depth=100&tmpId=1700574926103&soltab=range&gmff_type=basic&gmfft_sort_key=0&gmfft_sort_order=desc&board=Ks8h5d2cQc&cussol_id=0d48bd2d-6c6e-46c4-a0d4-25e50bb215c0&flop_actions=X-R1.3-R8.8-C&turn_actions=R22.6-C&history_spot=6&dialogs=solfigr-nodeedit-dialog_edit-solution_tmpNamespace-tmp%2Fprimary

        const percentageOfMax = logposition({ value: nd_percentage, min, max })
        const allin = this.numFixedUnit(nd_bb, 'bb') === this.numFixedUnit(max_bb, 'bb')

        this.l_actions[idx].percentageOfMax = percentageOfMax
        this.l_actions[idx].allin = allin
        this.l_actions[idx].display_name = allin ? 'Allin' : this.raiseIsBet ? 'Bet' : 'Raise'
        this.l_actions[idx].nd_bb = nd_bb
        this.l_actions[idx].nd_percentage = nd_percentage
      } else if (unit === 'e') {
        const { max_e } = this.unitMinMaxes
        const { min_e } = this.unitMinMaxes
        num = Math.max(Math.min(num, max_e), min_e)
        if (unitChange) {
          const hSpot = this.l_hSpot
          const dict = {
            flop: 3,
            turn: 2,
            river: 1,
          }
          num = dict[hSpot.street] || 3
        }
        this.l_actions[idx].nd_e = num
        this.l_actions[idx].display_name = this.raiseIsBet ? 'Bet' : 'Raise'
      } else if (unit === 'x') {
        const { max_x } = this.unitMinMaxes
        const { min_x } = this.unitMinMaxes
        num = Math.max(Math.min(num, max_x), min_x)
        this.l_actions[idx].nd_x = num
        this.l_actions[idx].display_name = this.raiseIsBet ? 'Bet' : 'Raise'
      }
    },
    isAllInBeforeAllin(aa, idx) {
      if (aa.type !== 'RAISE') return false
      if (!(aa.unit === '%' || aa.unit === 'bb')) return false
      const allinIdx = findLastIndex(this.l_actions, a => a.allin)
      return aa.allin && allinIdx > -1 && idx < allinIdx
    },
    isSameBetBeforeBet(aa, idx) {
      if (aa.type !== 'RAISE') return false
      if (aa.allin) return false
      if (!(aa.unit === '%' || aa.unit === 'bb')) return false

      const betIdx = findLastIndex(this.l_actions, a => a.type === 'RAISE')
      if (betIdx === idx) return false

      const bets = this.l_actions.filter((a, aIdx) => a.type === 'RAISE' && idx !== aIdx)
      const res = bets.some(a => a.nd_bb === aa.nd_bb)
      return res
    },
    async apply() {
      if (this.error) return
      if (this.loading) return
      if (!this.canApply) return
      if (this.loadingNewSol) return
      let hSpots = this.l_hSpots
      const hSpot = this.l_hSpot

      hSpots = hSpots.filter(i => i.id < hSpot.id)
      const prmSol = getHistoryUrlParams({
        history_spots: hSpots,
        includeShowdown: false,
      })
      const lastStreetIdx = all_streets.findIndex(i => i === hSpot.street)
      const startedStreets = all_streets.filter((i, idx) => idx <= lastStreetIdx)

      // b1, 1x, 1e, 1% 0 send 1% in bb - cos rounding errs
      // x % bb 1decimal
      // e 0decimal

      const nodeActions = this.l_actions.map(i => {
        if (i.type === 'RAISE') {
          if (
            isNaN(i.nd_x) || //
            isNaN(i.nd_e) ||
            isNaN(i.nd_percentage) ||
            isNaN(i.nd_bb)
          ) {
            console.error(`error-node-edit-NAN`)
          }
          return i.unit === 'x'
            ? `${this.numFixedUnit(i.nd_x, 'x')}x`
            : i.unit === 'e'
              ? `${this.numFixedUnit(i.nd_e, 'e')}e`
              : i.allin
                ? `all_in`
                : i.unit === '%'
                  ? `${this.numFixedUnit(i.nd_percentage * 100, 'bb_pot')}%`
                  : `b${this.numFixedUnit(i.nd_bb, 'bb')}`
        }
        return i.code
      })

      const { solElite } = this
      const parent_solution_id = hSpot.custom_solution_id || ''

      this.loadingNewSol = true
      if (this.editSolution) {
        const tree_operation = {
          actions: nodeActions,
          action_history: [
            solElite.is_preflop ? prmSol.preflop_actions : '',
            ...(startedStreets.includes('flop') ? [prmSol.flop_actions] : []),
            ...(startedStreets.includes('turn') ? [prmSol.turn_actions] : []),
            ...(startedStreets.includes('river') ? [prmSol.river_actions] : []),
          ],
        }
        const dataSolution = {
          tree_operations: solElite.cSol.tree_operations,
          last_tree_operation: tree_operation,
          parent_solution_id,
        }

        // !!!!
        // /solutions data on previous nodes (current-street-only) can be changed by nodeedit on current node
        // so clear all cached solution data - ideally just this street
        this.$store.dispatch('api_cache_clear', null, { root: true })

        await this.$store.dispatch('api_call', {
          method: 'post',
          data: dataSolution,
          endpoint: 'poker/custom-solutions/',
          action: SNK('slfrm_handleNodeUpdate', this.primaryNamespace),
          hasCancel: false,
        })
      }
      if (this.editTree) {
        // sending empty string here -- "" --
        // is not a mistake (it indicates that the street exists but no actions were made on it)
        const tree_operation = {
          actions: nodeActions,
          action_history: [
            prmSol.preflop_actions || '',
            ...(startedStreets.includes('flop') ? [prmSol.flop_actions] : []),
            ...(startedStreets.includes('turn') ? [prmSol.turn_actions] : []),
            ...(startedStreets.includes('river') ? [prmSol.river_actions] : []),
          ],
        }
        let treeOps = this.$store.getters[`${this.primaryNamespace}/slfrm_treeOperations`]
        treeOps = [...treeOps, tree_operation]
        this.$store.commit(`${this.primaryNamespace}/slfrm_set`, {
          key: 'slfrm_treeOperations',
          value: treeOps,
        })
        this.$store.dispatch('globals_action', 'tree_operations_update')
        // debugger
        this.$emit('close')
      }
      this.loadingNewSol = false
    },
    handleKeyPress(keys) {
      if (keys.includes('tab')) {
        const firstRaiseIdx = this.l_actions.findIndex(i => i.type === 'RAISE')
        if (!this.$refs[`input_${firstRaiseIdx}`]) return
        const el = document.activeElement
        if (!el?.tagName !== 'INPUT') {
          setTimeout(() => {
            this.$refs[`input_${firstRaiseIdx}`][0].focusInput()
          })
        }
      }
    },
    getBBPercent(aa) {
      return `${
        aa.unit === '%'
          ? this.numFixedUnit(aa.nd_bb, 'bb')
          : this.numFixedUnit(aa.nd_percentage * 100, 'bb_pot')
      }${aa.unit === '%' ? 'bb' : '%'}`
    },
    setLastUsedUnit(unit) {
      if (!['bb', '%'].includes(unit)) return

      this.lastUsedUnit = unit || ''
      localStorage.setItem('nodeedit_lastUsedUnit', unit)
    },
  },
  computed: {
    isPreflop() {
      return this.l_hSpot.street === 'preflop'
    },
    raiseIsBet() {
      return this.aaNodeedits?.legal_actions.some(a => a.display_name === 'BET')
    },
    showBetsIn() {
      return this.$store.getters.show_bets_in
    },
    unitsOptions() {
      // TODO get from solfigr conf
      // - but remember that flop in settings has no bb so maybe leave as is or move to solfigr_helpers to unify conf places
      return [
        'bb', //
        '%',
        // this.isPreflop ? '' : '%',
        // this.raiseIsBet ? '' : 'x',
        this.isPreflop ? 'x' : this.raiseIsBet ? '' : 'x',
        'e',
      ].filter(i => i)
    },
    unit() {
      let unit =
        this.lastUsedUnit ||
        (['bb', '%'].includes(localStorage.getItem('nodeedit_lastUsedUnit')) &&
          localStorage.getItem('nodeedit_lastUsedUnit')) ||
        {
          bb: 'bb',
          pot: '%',
          bb_and_pot: 'bb',
          pot_and_bb: '%',
        }[this.showBetsIn] ||
        'bb'

      unit = this.unitsOptions.includes(unit) ? unit : 'bb'
      return unit
    },
    raiseBetConf() {
      const { bet_sizes, raise_sizes } = this.conf
      const res = this.raiseIsBet ? bet_sizes : raise_sizes
      return res
    },
    solElite() {
      return this.$store.getters[`${this.namespace}/sol_elite`]
    },
    blinds() {
      if (this.editTree) {
        return {
          SB: this.$store.getters[`${this.namespace}/slfrm_blind`],
          BB: 1,
        }
      }
      return getBlinds(this.solElite.cTree.players)
    },
    unitMinMaxes() {
      const min_bb = this.numFixedUnit(Number(this.aaNodeedits?.min_bet || 0), 'bb')
      const max_bb = this.numFixedUnit(Number(this.aaNodeedits?.max_bet || 0), 'bb')

      const { street } = this.l_hSpot

      const min_percentage = getCurrentBetAsPotFromHistory({
        history_spots: this.l_hSpots,
        history_spot: this.l_hSpot,
        street,
        currentBet: min_bb,
        blinds: this.blinds,
      }).currentBetAsPot
      const max_percentage = getCurrentBetAsPotFromHistory({
        history_spots: this.l_hSpots,
        history_spot: this.l_hSpot,
        street,
        currentBet: max_bb,
        blinds: this.blinds,
      }).currentBetAsPot

      const conf = getSolfigrFields()
      const e_unit = conf.raise_sizes[
        `size_${street === 'preflop' ? 'preflop' : 'flop'}`
      ].by_unit.find(i => i.unit === 'e') // bet,raise = the same e defaults
      const x_unit = conf.raise_sizes[
        `size_${street === 'preflop' ? 'preflop' : 'flop'}`
      ].by_unit.find(i => i.unit === 'x') // bet,raise = the same x defaults

      const min_e = e_unit.min
      const max_e = e_unit.max
      const min_x = x_unit.min
      const max_x = x_unit.max

      const res = {
        min_bb,
        max_bb,
        min_percentage,
        max_percentage,
        min_percentage_input: min_percentage * 100,
        max_percentage_input: this.numFixedUnit(max_percentage * 100, 'bb_pot'),
        min_e,
        max_e,
        min_x,
        max_x,
        dd: this.aaNodeedits,
        e_unit,
      }
      // console.log(res)
      return res
    },
    onlyAllin() {
      return this.unitMinMaxes.min_bb === this.unitMinMaxes.max_bb
    },

    legalActions() {
      const legalActions = this.aaNodeedits?.legal_actions || []

      const dict = {
        check: {
          order: 1,
          type: 'CHECK',
          display_name: 'Check',
          code: 'X',
        },
        fold: {
          order: 1,
          type: 'FOLD',
          display_name: 'Fold',
          code: 'F',
        },
        call: {
          order: 2,
          type: 'CALL',
          display_name: 'Call',
          code: 'C',
        },
        raise: {
          order: 3,
          type: 'RAISE',
          nd_bb: this.unitMinMaxes.min_bb,
          nd_percentage: this.unitMinMaxes.min_percentage,
          percentageOfMax: logposition({
            value: this.unitMinMaxes.min_percentage,
            min: this.unitMinMaxes.min_percentage,
            max: this.unitMinMaxes.max_percentage,
          }),
          nd_e: 1,
          nd_x: 1,
          ...this.unitMinMaxes,
          unit: this.unit,
          allin: this.onlyAllin,
          display_name: this.onlyAllin ? 'Allin' : this.raiseIsBet ? 'Bet' : 'Raise',
        },
      }
      const res = []
      Object.keys(dict).forEach(k => {
        if (legalActions.some(la => la.type.toLowerCase() === k)) {
          const dictAction = dict[k]
          res.push(dictAction)
        }
      })
      return res
    },
    hasRaiseBet() {
      return (this.aaNodeedits?.legal_actions || []).some(a => a.type === 'RAISE')
    },
    betRaiseLimitReached() {
      // let limit = this.raiseIsBet ? 6 : 4
      const limit = this.raiseIsBet ? 9 : 6
      return this.l_actions.filter(a => a.type === 'RAISE').length >= limit
    },
    canApply() {
      return !!this.l_actions.length || this.loading
    },
  },
}
</script>

<style lang="scss" scoped>
.sigrndit {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  gap: 1rem;
  &_actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    gap: 0.125rem;
    gap: 2px;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }
  &_editnode {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    min-width: 0;
    flex: 1;
    border-radius: 4px;
    &_controls {
      display: flex;
      gap: 0.25rem;
    }
  }
  &_action {
    border-radius: 4px;
    background: var(--clr-black-1-3);
    padding: 0 0.25rem 0 0.75rem;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 2.5rem;
    &_left {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 0.5rem;
    }
    &_right {
      display: flex;
      margin-left: 0.5rem;
    }
    &_remove {
      font-size: 1.25rem;
      padding: 0.25rem;
      color: var(--clr-grey-2);
      &:hover {
        color: var(--clr-error);
      }
    }
    &_add {
      font-size: 1.25rem;
      padding: 0.25rem;
      color: var(--clr-primary);
    }
    &_name {
      display: flex;
      align-items: center;
      line-height: 1;
      font-weight: 500;
      min-width: 4rem;
    }
    &_bet {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      &_percent {
        width: 3.5rem;
        color: var(--clr-grey-2);
        font-weight: 600;
        font-size: 0.875rem;
      }
    }
    &_slider {
      min-width: 7rem;
    }
  }
}
</style>
